import React, { useState, useEffect, useCallback } from 'react';
import { BottomNav } from './components/BottomNav';
import { Sidebar } from './components/Sidebar';
import { FeedPage } from './pages/FeedPage';
import { ColorsPage } from './pages/ColorsPage';
import { ComposePage } from './pages/ComposePage';
import { ProfilePage } from './pages/ProfilePage';
import { EditProfilePage } from './pages/EditProfilePage';
import { PostDetailPage } from './pages/PostDetailPage';
import { SearchPage } from './pages/SearchPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { GoodbyePage } from './pages/GoodbyePage';
import { WelcomePage } from './pages/WelcomePage';
import { PrivacyPage } from './pages/legal/PrivacyPage';
import { TermsPage } from './pages/legal/TermsPage';
import { CookiesPage } from './pages/legal/CookiesPage';
import { useSession } from './hooks/useSession';
import { useAudio } from './contexts/AudioContext';

export type Page =
  | { id: 'feed' }
  | { id: 'colors' }
  | { id: 'post'; replyToId?: string }
  | { id: 'profile' }
  | { id: 'edit-profile' }
  | { id: 'user'; username: string }
  | { id: 'post-detail'; postId: string }
  | { id: 'search' }
  | { id: 'notifications' }
  | { id: 'goodbye' }
  | { id: 'welcome' }
  | { id: 'legal-privacy' }
  | { id: 'legal-terms' }
  | { id: 'legal-cookies' }
  | { id: 'not-found' };

// ── URL → Page ────────────────────────────────────────────────────────────────

function parsePath(pathname: string, search = ''): Page {
  const parts = pathname.replace(/^\//, '').split('/').filter(Boolean);
  const params = new URLSearchParams(search);

  if (parts[0] === 'goodbye') return { id: 'goodbye' };
  if (parts[0] === 'welcome') return { id: 'welcome' };
  if (parts[0] === 'legal' && parts[1] === 'privacy') return { id: 'legal-privacy' };
  if (parts[0] === 'legal' && parts[1] === 'terms')   return { id: 'legal-terms' };
  if (parts[0] === 'legal' && parts[1] === 'cookies') return { id: 'legal-cookies' };
  if (parts.length === 0 || parts[0] === 'feed') return { id: 'feed' };
  if (parts[0] === 'colors') return { id: 'colors' };
  if (parts[0] === 'search') return { id: 'search' };
  if (parts[0] === 'notifications') return { id: 'notifications' };
  if (parts[0] === 'post' && parts[1] === 'new') return { id: 'post', replyToId: params.get('reply_to') ?? undefined };
  if (parts[0] === 'posts' && parts[1]) return { id: 'post-detail', postId: parts[1] };
  if (parts[0] === 'profile' && parts[1] === 'edit') return { id: 'edit-profile' };
  if (parts[0] === 'profile') return { id: 'profile' };
  if (parts[0] === 'u' && parts[1]) return { id: 'user', username: parts[1] };

  return { id: 'not-found' };
}

function pageToPath(pageId: string, params?: Record<string, string>): string {
  if (pageId === 'goodbye')        return '/goodbye';
  if (pageId === 'welcome')        return '/welcome';
  if (pageId === 'legal-privacy')  return '/legal/privacy';
  if (pageId === 'legal-terms')    return '/legal/terms';
  if (pageId === 'legal-cookies')  return '/legal/cookies';
  if (pageId === 'feed')          return '/feed';
  if (pageId === 'colors')         return '/colors';
  if (pageId === 'search')        return '/search';
  if (pageId === 'notifications') return '/notifications';
  if (pageId === 'post')          return params?.reply_to ? `/post/new?reply_to=${params.reply_to}` : '/post/new';
  if (pageId === 'profile')       return '/profile';
  if (pageId === 'edit-profile')  return '/profile/edit';
  if (pageId === 'user' && params?.username) return `/u/${params.username}`;
  if (pageId === 'post-detail' && params?.postId) return `/posts/${params.postId}`;
  return '/feed';
}

// ── App ───────────────────────────────────────────────────────────────────────

function SpaceBarHandler() {
  const { togglePlayCallback } = useAudio();
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.code !== 'Space') return;
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'BUTTON') return;
      e.preventDefault();
      togglePlayCallback.current?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlayCallback]);
  return null;
}

export default function App() {
  const session = useSession();
  const [page, setPage] = useState<Page>(() => parsePath(window.location.pathname, window.location.search));
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // Keep page state in sync with browser back/forward
  useEffect(() => {
    function onPop() {
      setPage(parsePath(window.location.pathname, window.location.search));
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((pageId: string, params?: Record<string, string>) => {
    let nextPage: Page;
    if      (pageId === 'goodbye')       nextPage = { id: 'goodbye' };
    else if (pageId === 'welcome')       nextPage = { id: 'welcome' };
    else if (pageId === 'legal-privacy') nextPage = { id: 'legal-privacy' };
    else if (pageId === 'legal-terms')   nextPage = { id: 'legal-terms' };
    else if (pageId === 'legal-cookies') nextPage = { id: 'legal-cookies' };
    else if (pageId === 'feed')         nextPage = { id: 'feed' };
    else if (pageId === 'colors')        nextPage = { id: 'colors' };
    else if (pageId === 'post')         nextPage = { id: 'post', replyToId: params?.reply_to };
    else if (pageId === 'profile')      nextPage = { id: 'profile' };
    else if (pageId === 'edit-profile') nextPage = { id: 'edit-profile' };
    else if (pageId === 'search')        nextPage = { id: 'search' };
    else if (pageId === 'notifications') nextPage = { id: 'notifications' };
    else if (pageId === 'user' && params?.username) nextPage = { id: 'user', username: params.username };
    else if (pageId === 'post-detail' && params?.postId) nextPage = { id: 'post-detail', postId: params.postId };
    else nextPage = { id: 'feed' }; // internal navigations always resolve

    const path = pageToPath(pageId, params);
    window.history.pushState({}, '', path);
    setPage(nextPage);
  }, []);

  if (session.status === 'loading') return <Splash />;
  if (page.id === 'goodbye') return <GoodbyePage />;
  if (page.id === 'welcome') return <WelcomePage onDone={() => navigate('feed')} onNavigate={navigate} />;
  if (page.id === 'legal-privacy') return <PrivacyPage onBack={() => navigate('feed')} />;
  if (page.id === 'legal-terms')   return <TermsPage onBack={() => navigate('feed')} />;
  if (page.id === 'legal-cookies') return <CookiesPage onBack={() => navigate('feed')} />;

  const isAuthed = session.status === 'authenticated';
  const currentUser = isAuthed ? session.user : null;

  // /profile → /u/handle redirect once session is known
  if (page.id === 'profile' && currentUser?.username) {
    const path = `/u/${currentUser.username}`;
    window.history.replaceState({}, '', path);
    setPage({ id: 'user', username: currentUser.username });
    return null;
  }

  const needsAuth = !isAuthed && (page.id === 'post' || page.id === 'profile' || page.id === 'edit-profile' || page.id === 'notifications');
  if (needsAuth) return <LoginPage onBack={() => navigate('feed')} />;

  const isOwnProfile = page.id === 'user' && currentUser?.username === page.username;
  const navTab =
    page.id === 'post'          ? 'post' :
    page.id === 'colors'         ? 'colors' :
    page.id === 'search'        ? 'search' :
    page.id === 'notifications' ? 'notifications' :
    (page.id === 'profile' || page.id === 'edit-profile' || isOwnProfile) ? 'profile' :
    'feed';

  function handleLoginRequired() {
    setShowLoginPrompt(true);
  }

  return (
    <div className="app-layout">
      <SpaceBarHandler />
      <Sidebar current={navTab} onNavigate={navigate} user={currentUser} />

      <main className="main-content">
        {page.id === 'feed'          && <FeedPage onNavigate={navigate} onLoginRequired={handleLoginRequired} />}
        {page.id === 'colors'         && <ColorsPage onNavigate={navigate} onLoginRequired={handleLoginRequired} />}
        {page.id === 'search'        && <SearchPage onNavigate={navigate} onLoginRequired={handleLoginRequired} />}
        {page.id === 'notifications' && <NotificationsPage onNavigate={navigate} />}
        {page.id === 'post'          && <ComposePage onNavigate={navigate} replyToId={page.replyToId} />}
        {page.id === 'profile'       && <ProfilePage onNavigate={navigate} currentUser={currentUser} onLoginRequired={handleLoginRequired} />}
        {page.id === 'edit-profile'  && <EditProfilePage onNavigate={navigate} currentUser={currentUser} />}
        {page.id === 'user'          && <ProfilePage username={page.username} onNavigate={navigate} currentUser={currentUser} onLoginRequired={handleLoginRequired} />}
        {page.id === 'post-detail'   && <PostDetailPage postId={page.postId} onNavigate={navigate} currentUser={currentUser} onLoginRequired={handleLoginRequired} />}
        {page.id === 'not-found'     && <NotFoundPage onNavigate={navigate} />}
      </main>

      <BottomNav current={navTab} onNavigate={navigate} isAuthed={isAuthed} currentUsername={currentUser?.username} />

      {showLoginPrompt && (
        <LoginPrompt onClose={() => setShowLoginPrompt(false)} />
      )}
    </div>
  );
}

function LoginPrompt({ onClose }: { onClose: () => void }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--bg)', borderRadius: 'var(--radius)', padding: '28px 24px', maxWidth: 360, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, border: '1px solid var(--border)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Sign in to interact</p>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Resonate and reply to moments once you're logged in.
          </p>
        </div>
        <a
          href="/api/v1/auth/login"
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 24px', borderRadius: 100, background: '#fff', color: '#111', fontWeight: 600, fontSize: 14, boxShadow: '0 2px 16px rgba(0,0,0,0.3)' }}
        >
          <GoogleIcon />
          Continue with Google
        </a>
        <button onClick={onClose} style={{ fontSize: 13, color: 'var(--text-muted)' }}>Maybe later</button>
      </div>
    </div>
  );
}

function Splash() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, height: '100%' }}>
      <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', color: '#ff4d4d' }}>
        mylodic
      </h1>
      <div style={{ display: 'flex', gap: 6 }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
      </div>
    </div>
  );
}

export function LoginPage({ onBack }: { onBack?: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 32, padding: 32, height: '100%', width: '100%' }}>
      {onBack && (
        <button onClick={onBack} style={{ alignSelf: 'flex-start', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Back
        </button>
      )}
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: 42, fontWeight: 800, letterSpacing: '-0.04em', color: '#ff4d4d', lineHeight: 1.1 }}>
          mylodic
        </h1>
        <p style={{ marginTop: 10, fontSize: 15, color: 'var(--text-muted)', lineHeight: 1.6 }}>
          Sign in to post and interact.
        </p>
      </div>

      <a href="/api/v1/auth/login" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 28px', borderRadius: 100, background: '#fff', color: '#111', fontWeight: 600, fontSize: 15, boxShadow: '0 2px 20px rgba(0,0,0,0.4)' }}>
        <GoogleIcon />
        Continue with Google
      </a>

      <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
        By continuing you agree to our terms of service.
      </p>
    </div>
  );
}

function NotFoundPage({ onNavigate }: { onNavigate: (page: string) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16, padding: 32, textAlign: 'center' }}>
      <span style={{ fontSize: 48 }}>404</span>
      <p style={{ fontSize: 16, fontWeight: 600 }}>Page not found</p>
      <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>This page doesn't exist.</p>
      <button
        onClick={() => onNavigate('feed')}
        style={{ marginTop: 8, padding: '10px 24px', borderRadius: 100, background: 'var(--accent)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
      >
        Go home
      </button>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}
